/* eslint-disable no-magic-numbers */
/* eslint-disable no-case-declarations */
/* eslint-disable complexity */
import { HOUR_MINUTES } from '@futrli/shared-services';
import { get } from 'lodash';
import { deburr } from 'lodash';

import { FILTER_TYPE, SORT, TableFilters, TABLE_COLUMN, Data } from './filter-table-types';

interface FilterSubmitDisabledProps {
  checkedFilteredData?: Record<string, boolean>;
  fromValue?: string;
  toValue?: string;
  fromDate?: Date;
  toDate?: Date;
  fromTime?: string;
  toTime?: string;
  selectedValues?: Record<string, boolean>;
  activeColumn?: TABLE_COLUMN;
  filterType?: FILTER_TYPE;
}

/**
 * Gets current day time in format hh:mm and returns number of minutes from start of the day.
 * 19:34 -> 19 * 60 + 34 = 1174
 * @param time time in day in format hh:mm
 * @returns
 */
export const getNumberOfMinutesFromStartOfDay = (time?: string) => {
  if (!time) {
    return -1;
  }

  if (!HOUR_MINUTES.test(time)) {
    return -1;
  }

  const hours = Number(time.split(':')[0]);
  const minutes = Number(time.split(':')[1]);

  return hours * 60 + minutes;
};

/**
 * Checks if specific string contains specific term
 * @param originalString
 * @param term
 * @returns {Boolean}
 */
export const checkTermInString = (originalString: string, term: string): boolean => {
  return deburr(originalString.toLowerCase()).indexOf(deburr(term.toLowerCase())) !== -1;
};

/**
 * Checks if data passes all filters.
 * @param data current data
 * @param filters object containing all filters
 * @returns {Boolean}
 */
export const checkIfPassesFilters = (data: Data, filters: TableFilters): boolean => {
  const filterTableColumns = Object.keys(filters) as TABLE_COLUMN[];
  for (const filterColumn of filterTableColumns) {
    const dataValue = data[filterColumn];
    if (typeof dataValue === 'undefined') {
      continue;
    }

    if (filters[filterColumn].filterType === FILTER_TYPE.SELECT) {
      const selectedValues = get(filters, `${filterColumn}.selectedValues`);
      if (typeof selectedValues !== 'undefined' && !selectedValues[dataValue as string]) {
        return false;
      }
    }

    if (filters[filterColumn].filterType === FILTER_TYPE.TEXT) {
      const searchQuery = get(filters, `${filterColumn}.searchQuery`);
      const checkedFilteredData = get(filters, `${filterColumn}.checkedFilteredData`);

      if (!checkTermInString(dataValue as string, searchQuery || '')) {
        return false;
      }
      if (typeof checkedFilteredData !== 'undefined' && !checkedFilteredData[data.id]) {
        return false;
      }
    }

    if (filters[filterColumn].filterType === FILTER_TYPE.NUMBER) {
      const fromValue = get(filters, `${filterColumn}.fromValue`);
      const toValue = get(filters, `${filterColumn}.toValue`);

      if (typeof dataValue === 'number') {
        if (typeof fromValue === 'number' && typeof toValue === 'number') {
          if (dataValue < fromValue || dataValue > toValue) {
            return false;
          }
        } else if (typeof fromValue === 'number') {
          if (dataValue < fromValue) {
            return false;
          }
        } else if (typeof toValue === 'number') {
          if (dataValue > toValue) {
            return false;
          }
        }
      }
    }

    if (filters[filterColumn].filterType === FILTER_TYPE.DATE) {
      const dataValueDate = dataValue as Date;
      const fromDate = get(filters, `${filterColumn}.fromDate`) as unknown as Date;
      const toDate = get(filters, `${filterColumn}.toDate`) as unknown as Date;

      if (typeof fromDate !== 'undefined' && typeof toDate !== 'undefined') {
        if (dataValueDate.getTime() < fromDate.getTime() || dataValueDate.getTime() > toDate.getTime()) {
          return false;
        }
      } else if (typeof fromDate !== 'undefined') {
        if (dataValueDate.getTime() < fromDate.getTime()) {
          return false;
        }
      } else if (typeof toDate !== 'undefined') {
        if (dataValueDate.getTime() > toDate.getTime()) {
          return false;
        }
      }
    }

    if (filters[filterColumn].filterType === FILTER_TYPE.TIME) {
      const dataValueStr = dataValue as string;
      const fromTime = get(filters, `${filterColumn}.fromTime`) as unknown as string;
      const toTime = get(filters, `${filterColumn}.toTime`) as unknown as string;

      const fromMinutesFromStartOfDay = getNumberOfMinutesFromStartOfDay(fromTime);
      const toMinutesFromStartOfDay = getNumberOfMinutesFromStartOfDay(toTime);
      const dataValueMinutesFromStartOfDay = getNumberOfMinutesFromStartOfDay(dataValueStr);

      if (fromMinutesFromStartOfDay >= 0 && toMinutesFromStartOfDay >= 0) {
        if (
          dataValueMinutesFromStartOfDay < fromMinutesFromStartOfDay ||
          dataValueMinutesFromStartOfDay > toMinutesFromStartOfDay
        ) {
          return false;
        }
      } else if (fromMinutesFromStartOfDay >= 0) {
        if (dataValueMinutesFromStartOfDay < fromMinutesFromStartOfDay) {
          return false;
        }
      } else if (toMinutesFromStartOfDay >= 0) {
        if (dataValueMinutesFromStartOfDay > toMinutesFromStartOfDay) {
          return false;
        }
      }
    }
  }
  return true;
};

/**
 * Returns sorted data according to activeColumn and sort
 * @param data list of data to be sorted
 * @param activeColumn column by which data are sorted
 * @param sort determines ASC or DESC sort
 * @returns {Data[]}
 */
export const getSortedData = (data: Data[], sortByColumn: TABLE_COLUMN, sort: SORT, filterType: FILTER_TYPE) => {
  if (filterType === FILTER_TYPE.NUMBER) {
    return data
      .sort((a, b) => {
        const dataValueA = a[sortByColumn] as number;
        const dataValueB = b[sortByColumn] as number;
        return sort === SORT.ASC ? dataValueA - dataValueB : dataValueB - dataValueA;
      })
      .concat();
  } else if (filterType === FILTER_TYPE.DATE) {
    return data
      .sort((a, b) => {
        const dataValueA = a[sortByColumn] as Date;
        const dataValueB = b[sortByColumn] as Date;
        return sort === SORT.ASC
          ? dataValueA.getTime() - dataValueB.getTime()
          : dataValueB.getTime() - dataValueA.getTime();
      })
      .concat();
  } else if (filterType === FILTER_TYPE.TIME) {
    return data
      .sort((a, b) => {
        const timeValueA = a[sortByColumn] as string;
        const timeValueB = b[sortByColumn] as string;
        const minutesFromStartOfDayA = getNumberOfMinutesFromStartOfDay(timeValueA);
        const minutesFromStartOfDayB = getNumberOfMinutesFromStartOfDay(timeValueB);
        return sort === SORT.ASC
          ? minutesFromStartOfDayA - minutesFromStartOfDayB
          : minutesFromStartOfDayB - minutesFromStartOfDayA;
      })
      .concat();
  } else {
    return data
      .sort((a, b) => {
        const dataValueA = a[sortByColumn] as string;
        const dataValueB = b[sortByColumn] as string;
        return sort === SORT.ASC ? dataValueA.localeCompare(dataValueB) : dataValueB.localeCompare(dataValueA);
      })
      .concat();
  }
};

/**
 * Checking does filters containing data for specific column
 * @param filters object containing all filters
 * @param column column which is checked in filters object
 * @param sortByColumn column by which data are sorted
 * @returns {Boolean}
 */
export const isFilterByColumn = (filters: TableFilters, column: TABLE_COLUMN, sortByColumn?: TABLE_COLUMN) => {
  if (sortByColumn === column) {
    return true;
  }

  if (typeof filters[column] === 'undefined') {
    return false;
  }

  switch (filters[column].filterType) {
    case FILTER_TYPE.NUMBER:
      return (
        typeof get(filters, `${column}.fromValue`) === 'number' || typeof get(filters, `${column}.toValue`) === 'number'
      );
    case FILTER_TYPE.DATE:
      return (
        typeof get(filters, `${column}.fromDate`) !== 'undefined' ||
        typeof get(filters, `${column}.toDate`) !== 'undefined'
      );
    case FILTER_TYPE.TIME:
      return (
        typeof get(filters, `${column}.fromTime`) !== 'undefined' ||
        typeof get(filters, `${column}.toTime`) !== 'undefined'
      );
    case FILTER_TYPE.SELECT:
      const selected = get(filters, `${column}.selectedValues`);
      if (selected) {
        return Object.keys(selected).filter(s => !selected[s]).length > 0;
      }
      return false;
    case FILTER_TYPE.TEXT:
      const newSearchQuery = get(filters, `${column}.searchQuery`);
      return typeof newSearchQuery === 'string' && (newSearchQuery as string).length > 0;
    default:
      return false;
  }
};

/**
 * Checks if Clear label is enabled when filter is open
 * @param filters object containing all filters
 * @param sortByColumn column by which data are sorted
 * @param activeColumn column which filter is currently opened
 * @returns {Boolean}
 */
export const isFilterClearEnabled = (
  filters: TableFilters,
  sortByColumn?: TABLE_COLUMN,
  activeColumn?: TABLE_COLUMN
) => {
  if (!activeColumn) {
    return false;
  }

  if (activeColumn === sortByColumn) {
    return true;
  }

  switch (filters[activeColumn].filterType) {
    case FILTER_TYPE.NUMBER:
      return (
        typeof get(filters, `${activeColumn}.fromValue`) === 'number' ||
        typeof get(filters, `${activeColumn}.toValue`) === 'number'
      );
    case FILTER_TYPE.DATE:
      return (
        typeof get(filters, `${activeColumn}.fromDate`) !== 'undefined' ||
        typeof get(filters, `${activeColumn}.toDate`) !== 'undefined'
      );
    case FILTER_TYPE.TIME:
      return (
        typeof get(filters, `${activeColumn}.fromTime`) !== 'undefined' ||
        typeof get(filters, `${activeColumn}.toTime`) !== 'undefined'
      );
    case FILTER_TYPE.SELECT:
      const selected = get(filters, `${activeColumn}.selectedValues`);
      if (selected) {
        return Object.keys(selected).filter(s => !selected[s]).length > 0;
      }
      return false;
    case FILTER_TYPE.TEXT:
      const newSearchQuery = get(filters, `${activeColumn}.searchQuery`);
      return typeof newSearchQuery === 'string' && (newSearchQuery as string).length > 0;

    default:
      return true;
  }
};

/**
 * Checks if filter submit is disabled
 * @returns {Boolean}
 */
export const isFilterSubmitDisabled = ({
  checkedFilteredData,
  fromValue,
  toValue,
  fromDate,
  toDate,
  fromTime,
  toTime,
  selectedValues,
  activeColumn,
  filterType
}: FilterSubmitDisabledProps) => {
  if (!activeColumn) {
    return true;
  }
  if (filterType === FILTER_TYPE.TEXT) {
    if (!checkedFilteredData) {
      return true;
    }
    return Object.keys(checkedFilteredData).filter(id => checkedFilteredData[id]).length === 0;
  }
  if (filterType === FILTER_TYPE.NUMBER) {
    if (!fromValue && !toValue) {
      return true;
    }

    if (fromValue && toValue && Number(toValue) < Number(fromValue)) {
      return true;
    }

    return false;
  }

  if (filterType === FILTER_TYPE.DATE) {
    if (!fromDate && !toDate) {
      return true;
    }

    if (fromDate && toDate && toDate.getTime() < fromDate.getTime()) {
      return true;
    }

    return false;
  }

  if (filterType === FILTER_TYPE.TIME) {
    if (!fromTime && !toTime) {
      return true;
    }

    if (fromTime && !HOUR_MINUTES.test(fromTime)) {
      return true;
    }

    if (toTime && !HOUR_MINUTES.test(toTime)) {
      return true;
    }

    const fromHours = fromTime ? Number(fromTime.split(':')[0]) : -1;
    const fromMinutes = fromTime ? Number(fromTime.split(':')[1]) : -1;

    const toHours = toTime ? Number(toTime.split(':')[0]) : -1;
    const toMinutes = toTime ? Number(toTime.split(':')[1]) : -1;

    if (fromHours >= 0 && fromMinutes >= 0 && toHours >= 0 && toMinutes >= 0) {
      const fromMinutesFromStartOfDay = fromHours * 60 + fromMinutes;
      const toMinutesFromStartOfDay = toHours * 60 + toMinutes;
      return fromMinutesFromStartOfDay > toMinutesFromStartOfDay;
    }
  }

  if (filterType === FILTER_TYPE.SELECT) {
    if (!selectedValues) {
      return true;
    }
    return Object.keys(selectedValues).filter(s => selectedValues[s]).length === 0;
  }
};
